/**
 * What a map screen asks of the map, whichever engine draws it.
 *
 * Google Maps when the API sends a browser key, MapLibre on OpenFreeMap when it
 * does not (ADR 020). The screens hand over pins and lines and never touch
 * either library, so switching engines is a configuration change.
 */

/** Longitude first, as GeoJSON and MapLibre write it. */
export type LngLat = [number, number]

export interface MapPin {
  id: string
  lngLat: LngLat
  /** Drawn centred on the point. The screen owns it, so it can restyle it in place. */
  element: HTMLElement
}

export interface MapLine {
  colour: string
  path: LngLat[]
}

/** A polygon feature collection whose features carry a string `id` property. */
export interface ShapeCollection {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    properties: { id: string } & Record<string, unknown>
    geometry:
      | { type: 'Polygon'; coordinates: LngLat[][] }
      | { type: 'MultiPolygon'; coordinates: LngLat[][][] }
  }>
}

export interface ShapeHandlers {
  onClick: (id: string) => void
  /** Null when the pointer leaves every shape. */
  onHover: (id: string | null) => void
}

/**
 * A pin is a button inside the map's own pane. Focusing it makes the browser
 * scroll that pane, which reads as the map jumping, and the click continuing
 * on to the map can move it too. The holder swallows both; the button's own
 * click still runs.
 */
export function pinHolder(element: HTMLElement): HTMLElement {
  const holder = document.createElement('div')
  holder.addEventListener('mousedown', (event) => event.preventDefault())
  holder.addEventListener('click', (event) => event.stopPropagation())
  holder.append(element)
  return holder
}

/** Same places, whatever order the pins and lines arrived in. */
export function placesKey(points: readonly LngLat[]): string {
  const unique = new Set<string>()
  for (const [lng, lat] of points) unique.add(`${lng},${lat}`)
  return [...unique].sort().join(';')
}

export interface MapSurface {
  setPins: (pins: MapPin[]) => void
  setLines: (lines: MapLine[]) => void
  /** Clickable outlines, such as suburbs. Null removes them. */
  setShapes: (shapes: ShapeCollection | null, handlers: ShapeHandlers) => void
  /** Fill by shape id; a shape not in the map is outline only. Cheap to call often. */
  fillShapes: (fills: ReadonlyMap<string, string>) => void
  /** Frame these points, no closer than street level. Nothing to frame leaves the view alone. */
  fit: (points: LngLat[]) => void
  destroy: () => void
}

/** Perth, so an empty map is the business's own city rather than the ocean. */
export const FALLBACK_CENTRE: LngLat = [115.8605, -31.9505]

export const FIT_PADDING_PX = 48
export const MAX_FIT_ZOOM = 14

export const SHAPE_OUTLINE = '#475569'
export const SHAPE_FILL_OPACITY = 0.45
export const SHAPE_HOVER_OPACITY = 0.65
